import h from 'hyperscript';

import { createAbortError } from '@disy/lib/abort-error';
import { assertNonNullable, requireNonNullable } from '@disy/lib/custom-error';
import { debounce } from '@disy/lib/debounce';
import { on } from '@disy/lib/event-util';
import { cadenzaSessionStorage } from '@disy/lib/storage';
import { type Teardown, tearDown } from '@disy/lib/teardown';
import { deleteUrlParam, getUrlParam, hasUrlParam } from '@disy/lib/url-param-util';
import '@disy/ui/content-font-variables.css';
import { getSetting } from '@disy/workbook.lib/settings';

import { invalidateCachesForObjecttypes } from 'cadenza/api-client/objecttypes-api';
import { handleRequest, subscribeToEvent } from 'cadenza/integration/post-message';
import { isActiveWorksheetOnDemand, isViewOnDemand } from 'cadenza/map/on-demand/on-demand-map.state';
import type { RootState, RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import type { PartialContext } from 'cadenza/ui/link/model/partial-context.gen';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { AnalysisContextVisibilityMode } from 'cadenza/workbook/model/analysis-context-visibility-mode.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key.gen';
import { SelectionCart } from 'cadenza/workbook/selection-cart/selection-cart';
import { hidePanel, showPanel } from 'cadenza/workbook/state/workbook-panel-actions.state';
import { isPanelVisible } from 'cadenza/workbook/state/workbook-panel.state';
import { View } from 'cadenza/workbook/view/view';
import { isViewConfigComplete } from 'cadenza/workbook/view/view-config-utils';
import { COMPONENT_NAME as EMPTY_VIEW_ELEMENT_NAME, EmptyView } from 'cadenza/workbook/view/view-empty/empty-view';
import {
  areRequiredConditionsValid,
  getActiveWorksheet,
  getActiveWorksheetId,
  getActiveWorksheetViews,
  getInvalidViewsOrDataViewsOfActiveWorksheet,
  getMainObjecttypeIdsForActiveWorksheet,
  getMaximizedViewId,
  getViewById,
  getWorksheetBackgroundColor,
  isAnyViewLoadingOrRendering,
  isIntegratedView,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import {
  activateView,
  initializeWorksheet,
  invalidateDataViewsUsingObjecttypes,
  loadOnDemandWorksheet,
} from 'cadenza/workbook/workbook.state';
import { OnDemandOverlay } from 'cadenza/workbook/worksheet/on-demand-overlay/on-demand-overlay';
import { VIEW_POPUP_CLASS, WorksheetGrid } from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-grid';
import { toggleViewBorder } from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';
import { calculateWorksheetViewBorders } from 'cadenza/workbook/worksheet/worksheet-util';

import './worksheet.css';

const COMPONENT_NAME = 'd-worksheet';

const URL_PARAM_SKIP_ON_DEMAND = 'skipOnDemand';

export const NEW_VIEW_ID_KEY = 'workbookNewViewId';

export class Worksheet extends HTMLElement {
  #initialized?: boolean;
  readonly #integratedViews: View[] = [];
  #onDemandOverlay?: OnDemandOverlay;
  #teardowns: Teardown[] = [];
  #worksheetGrid?: WorksheetGrid;

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.dataset.id = getActiveWorksheetId(getStore().getState()); // for e2e tests
    if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-titlebar')) {
      this.classList.add(VIEW_POPUP_CLASS);
    }
    this.#observeWorksheetChanges();
    this.#observeAnalysisContextPanelVisible();
    this.#observeViewsReloadForCadenzajs();
    this.#observeWorksheetResize();
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
  }

  #observeWorksheetChanges() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        getActiveWorksheet,
        (worksheet) => {
          // CADENZA-27839 - only try to load the worksheet if it still exists
          if (worksheet) {
            this.#updateFromStore(worksheet);
          }
        },
        true,
      ),
    );
  }

  #updateFromStore({ views }: WorksheetDto) {
    const viewsToLoad = getViewsToLoad(views);
    if (!this.#initialized && typeof viewsToLoad !== 'undefined') {
      this.#initWorksheetGrid(viewsToLoad);
      // initialize the selection cart here because we want to append it only when worksheet is there
      if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-selection-cart')) {
        this.append(new SelectionCart());
      }
      this.#initialized = true;
    }
    assertNonNullable(this.#worksheetGrid);

    if (typeof viewsToLoad !== 'undefined') {
      let informGridAboutChangedViews = false;

      const emptyView = document.querySelector(EMPTY_VIEW_ELEMENT_NAME);
      if (viewsToLoad.length && emptyView) {
        emptyView.remove();
        getStore().dispatch(showPanel(WorkbookPanelName.VIEW_SETTINGS));
        informGridAboutChangedViews = true;
      } else if (!viewsToLoad.length && !emptyView) {
        this.#worksheetGrid.add(new EmptyView());
      }

      informGridAboutChangedViews ||= this.#removeDeletedViews(views);

      if (informGridAboutChangedViews) {
        this.#worksheetGrid.onViewChanged();
      }
      this.#createNewViewIfNotInDOM(viewsToLoad);
    }
  }

  #initWorksheetGrid(viewIds: ViewId[]) {
    const worksheetViews = viewIds.filter(isNotIntegrated).map((viewId) => new View(viewId));
    this.#worksheetGrid = new WorksheetGrid(worksheetViews);
    this.#onDemandOverlay = new OnDemandOverlay();
    on(this.#onDemandOverlay, 'ondemand:load', () => this.#loadWorksheet());
    this.append(h('.on-demand-parent', [this.#worksheetGrid, this.#onDemandOverlay]));

    this.#teardowns.push(
      observeStore(getStore(), getWorksheetBackgroundColor, (color) => this.#applyColors(color)),
      observeStore(getStore(), getWorksheetViewBorders, (showBorders) => this.#applyWorksheetViewBorders(showBorders)),
      observeStore(
        getStore(),
        getInvalidViewsOrDataViewsOfActiveWorksheet,
        (viewsAndDataViews) => this.#invalidateOrReloadIfNecessary(viewsAndDataViews),
        true, // compareStateByValue needed as the array is always created new, but the content might be the same
      ),
    );
  }

  #loadWorksheet() {
    getStore().dispatch(loadOnDemandWorksheet());
    this.views.forEach((view) => view.reload(true));
    getStore().dispatch(initializeWorksheet());
  }

  #applyColors(backgroundColor: string) {
    this.style.backgroundColor = backgroundColor;
  }

  #applyWorksheetViewBorders(showBorders: boolean) {
    this.#worksheetGrid?.views.forEach((v) => toggleViewBorder(v, showBorders));
  }

  #invalidateOrReloadIfNecessary(viewsAndDataViews: (ViewId | DataViewId)[]) {
    const dirty = viewsAndDataViews.length > 0;
    assertNonNullable(this.#onDemandOverlay);
    if (hasUrlParam(URL_PARAM_SKIP_ON_DEMAND)) {
      this.#onDemandOverlay.hidden = true;
      setTimeout(() => {
        // timeout is necessary to avoid infinite loop
        this.#reloadOnDemandViews();
      });
    } else if (
      dirty &&
      isActiveWorksheetOnDemand(getStore().getState()) &&
      areRequiredConditionsValid(getStore().getState())
    ) {
      this.#onDemandOverlay.hidden = false;
    }
    deleteUrlParam(URL_PARAM_SKIP_ON_DEMAND);
  }

  #removeDeletedViews(filteredViews: ViewId[]) {
    return this.views.reduce((viewsWereRemoved, view) => {
      if (!filteredViews.includes(view.viewId)) {
        view.remove();
        return true;
      }
      return viewsWereRemoved;
    }, false);
  }

  #createNewViewIfNotInDOM(filteredViews: ViewId[]) {
    assertNonNullable(this.#worksheetGrid);
    filteredViews.filter(isNotIntegrated).forEach((viewId) => {
      if (!this.getViewById(viewId)) {
        const newView = new View(viewId);
        this.#worksheetGrid!.add(newView);
        toggleViewBorder(newView, getWorksheetViewBorders(getStore().getState()));
        getStore().dispatch(activateView(newView.viewId));
        cadenzaSessionStorage.setItem(NEW_VIEW_ID_KEY, newView.viewId);
      }
    });
  }

  #reloadOnDemandViews() {
    const activeViewsOfWorksheet = getActiveWorksheetViews(getStore().getState());
    const viewsToLoadManually =
      isActiveWorksheetOnDemand(getStore().getState()) ? activeViewsOfWorksheet : (
        activeViewsOfWorksheet.filter((view) => isViewOnDemand(getStore().getState(), view.id))
      );
    viewsToLoadManually
      .map((viewDto) => viewDto.id)
      .forEach((viewId) => {
        const view = this.getViewById(viewId);
        if (view) {
          view.reload(true);
        }
      });
  }

  #observeAnalysisContextPanelVisible() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => this.#getAnalysisContextVisibilitySetting(state),
        (analysisContextVisibilityModeValue) =>
          this.#toggleAnalysisContextPanelVisible(analysisContextVisibilityModeValue),
      ),
    );
  }

  #getAnalysisContextVisibilitySetting(state: RootState) {
    return getSetting(
      getActiveWorksheet(state),
      WorksheetSettingsKey.toPersistenceKey('ANALYSIS_CONTEXT_VISIBILITY'),
      'auto',
    );
  }

  #toggleAnalysisContextPanelVisible(value: string) {
    const analysisContextVisibilityMode = AnalysisContextVisibilityMode.fromPersistenceKey(value);
    const isVisible = isPanelVisible(getStore().getState(), WorkbookPanelName.ANALYSIS_CONTEXT);
    if (analysisContextVisibilityMode === 'ALWAYS_VISIBLE' && !isVisible) {
      getStore().dispatch(showPanel(WorkbookPanelName.ANALYSIS_CONTEXT));
    } else if (analysisContextVisibilityMode === 'ALWAYS_HIDDEN' && isVisible) {
      getStore().dispatch(hidePanel(WorkbookPanelName.ANALYSIS_CONTEXT));
    }
  }

  #observeViewsReloadForCadenzajs() {
    let currentReject: PromiseWithResolvers<void>['reject'] | undefined;
    this.#teardowns.push(
      subscribeToEvent('reload', async (event) => {
        if (currentReject) {
          currentReject(createAbortError());
        }

        const { promise, resolve, reject } = Promise.withResolvers<void>();
        currentReject = reject;
        handleRequest(event, promise);

        const views = this.views;
        if (!views.some((view) => view.type !== 'TEXT' && isViewConfigComplete(view.type, view.dataViews[0]))) {
          return resolve(); // nothing to reload
        }

        const store = getStore();
        if (event.detail.invalidateCaches) {
          const mainObjecttypeIds = getMainObjecttypeIdsForActiveWorksheet(store.getState());
          await invalidateCachesForObjecttypes(mainObjecttypeIds);
          store.dispatch(invalidateDataViewsUsingObjecttypes({ objecttypeIds: mainObjecttypeIds }));
        }
        views.forEach((view) => view.reload(true));

        // Debounce is used here because the loading state changes multiple times due to embedded-view-update-strategy viewLoadingState listener
        const unsubscribe = observeStore(
          store,
          isAnyViewLoadingOrRendering,
          debounce((loading) => {
            if (!loading) {
              resolve();
            }
          }),
        );
        promise.finally(() => {
          currentReject = undefined;
          unsubscribe();
        });
      }),
    );
  }

  #observeWorksheetResize() {
    const resizeObserver = new ResizeObserver(
      ([
        {
          target,
          contentRect: { width, height },
        },
      ]) => {
        const worksheet = target as Worksheet;
        const windowHeight = window.innerHeight;
        worksheet.style.setProperty('--width', String(width));
        worksheet.style.setProperty('--height', String(height > windowHeight ? windowHeight : height));
      },
    );
    resizeObserver.observe(this);
    this.#teardowns.push(resizeObserver);
  }

  /** @deprecated Use `getActiveWorksheetViews()` from `workbook.state.js` instead */
  get views(): View[] {
    return [...new Set([...this.querySelectorAll<View>('.d-worksheet-view'), ...this.#integratedViews])];
  }

  /** @deprecated Use `getViewById()` from `workbook.state.js` instead */
  getViewById(viewId: ViewId) {
    return this.views.find((view) => view.viewId === viewId);
  }

  insertBeforeOnDemandOverlay(element: HTMLElement) {
    assertNonNullable(this.#onDemandOverlay);
    const parent = this.querySelector('.on-demand-parent');
    assertNonNullable(parent);
    parent.insertBefore(element, this.#onDemandOverlay);
  }

  /**
   * Integrated views should be lazily loaded from the place where they are put, because they may not be visible
   * from the start. For example integrated overview map may be hidden when layout option for the main map is ONLY_MAP
   */
  getOrCreateIntegratedView(viewId: ViewId) {
    let view = this.getIntegratedView(viewId);
    if (!view) {
      view = new View(viewId);
      this.#integratedViews.push(view);
    }
    return view;
  }

  getIntegratedView(viewId: ViewId) {
    return this.#integratedViews.find((view) => view.viewId === viewId);
  }

  integrateViewFromGrid(viewId: ViewId) {
    const view = this.getViewById(viewId);
    if (view) {
      this.#integratedViews.push(view);
      this.#worksheetGrid?.onViewChanged();
    }
  }

  detachIntegratedView(viewId: ViewId) {
    // View may not be in this.#integratedViews if map layout is ONLY_MAP.
    const viewIdx = this.#integratedViews.findIndex((v) => v.viewId === viewId);
    const view: View = viewIdx === -1 ? new View(viewId) : this.#integratedViews[viewIdx];

    const viewDto = getViewById(getStore().getState(), viewId);
    view.setLayoutOnClient([...requireNonNullable(viewDto.layout)]); // After detaching, the view must have a layout
    this.#integratedViews.splice(viewIdx, 1);
    this.#worksheetGrid?.add(view);
    this.#worksheetGrid?.onViewChanged();
  }
}

function getViewsToLoad(views: ViewId[]) {
  const maximizedViewId = getMaximizedViewId(getStore().getState());
  let viewsToLoad = maximizedViewId && !couldShowOtherViews() ? [maximizedViewId] : views;
  viewsToLoad = viewsToLoad.filter(isNotIntegrated);
  return viewsToLoad;
}

/** if we are in a context where the 'restore' button is available on views to show other views */
function couldShowOtherViews() {
  const partialContext = getUrlParam<PartialContext>('partialContext');
  return !(
    partialContext === 'viewPopup' ||
    partialContext === 'viewEmbedding' ||
    partialContext === 'cadenzaApiMapDialog'
  );
}

function isNotIntegrated(viewId: ViewId) {
  return !isIntegrated(viewId);
}

function isIntegrated(viewId: ViewId) {
  return isIntegratedView(getStore().getState(), viewId);
}

const getWorksheetViewBorders = (state: RootStateOr<WorkbookState>, worksheetId?: WorksheetId) => {
  return calculateWorksheetViewBorders(worksheetId ?? getActiveWorksheetId(state), state);
};

customElements.define(COMPONENT_NAME, Worksheet);
